import logo from '@assets/appImages/favicon.png';
import { useAIAssistantSelector, useAIAssistantActions, ApprovalCard, ReportPreviewCard } from '@domains/aiAssistant';
import { getMessageBlockRenderer } from '@domains/aiAssistant/state/messageBlocks';
// Registers every block renderer this app emits. Imported for the side effect —
// the registry is populated at module load, before the first message renders.
import './blocks';
import type { AIChatMessage } from '@domains/aiAssistant';
import { ActionIcon, Button, Image, Text, Textarea, Tooltip } from '@mantine/core';
import { IconHash, IconHistory, IconMessage, IconMessagePlus, IconPlayerStopFilled, IconSend, IconSparkles, IconThumbDown, IconThumbUp } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './AIAssistantChat.module.css';
import { capitalizeFirstLetter } from '@utils/strings';
import { CommandPlanCard } from './CommandPlanCard';
import { MarkdownMessage } from './MarkdownMessage';
import { AutoExecuteToggle } from './AutoExecuteToggle';
import { componentsTestIds } from '@components/testids';

interface MessageProps {
  message: AIChatMessage;
  formatTimestamp: ( timestamp: number ) => string;
}

const UserMessage: React.FC<MessageProps> = ({ message, formatTimestamp }) => (
  <div
    className={`${classes.messageItem} ${classes.userMessage}`}
    data-testid={componentsTestIds.aiChatMessageUser( message.id )}
  >
    {/*
      Deliberately PLAIN TEXT, not Markdown. Echoing a user's own `#`, `*` or
      `<b>` back at them as formatting silently rewrites what they typed, and
      the user's turn is not the thing this surface is rendering — see
      `MarkdownMessage`.
    */}
    <div>{message.content}</div>
    <div className={`${classes.timestamp} ${classes.userTimestamp}`}>
      {formatTimestamp( message.timestamp )}
    </div>
  </div>
);

const AssistantMessage: React.FC<MessageProps> = ({ message, formatTimestamp }) => {
  const processingStep = useAIAssistantSelector( s => s.processingStep );
  const { submitFeedback } = useAIAssistantActions();
  const { t } = useTranslation( 'interactions' );
 return <div
    className={`${classes.messageItem} ${classes.assistantMessage}`}
    data-testid={componentsTestIds.aiChatMessageAssistant( message.id )}
  >
    <div className={classes.messageSender}>
      <Image src={logo} alt="Assistant" width='auto' height={16} />
      <span>Tessy</span>
    </div>

    {message.isLoading && (
      <div className={classes.thinkingChip} data-testid={componentsTestIds.aiChatStreamingIndicator}>
        <IconSparkles size={14} />
        <span>{capitalizeFirstLetter( processingStep )}</span>
        <div className={classes.loadingDots}>
          <span></span>
          <span></span>
        </div>
      </div>
    )}

    <div className={classes.messageContent}>
      <MarkdownMessage content={message.content} isStreaming={Boolean( message.isLoading )} />
      {/*
        Rich block, resolved through the registry. An unknown type renders
        nothing — the message text above still stands on its own, which is the
        right degradation for a client build that predates a new block type.
      */}
      {message.block && !message.isLoading && (() => {
        const BlockRenderer = getMessageBlockRenderer( message.block.type );
        return BlockRenderer
          ? <BlockRenderer block={message.block} messageId={message.id} />
          : null;
      })()}
      {message.commandPlan && !message.isLoading && (
        <CommandPlanCard message={message} />
      )}
      {message.reportPreview && !message.isLoading && (
        <ReportPreviewCard message={message} />
      )}
      {/*
        Thumbs up/down (TV1-2417). These buttons existed here with no `onClick` — they
        rendered, they hovered, and they did nothing. Now they dispatch.

        ## They are ALWAYS SHOWN, never conditioned on `message.traceId`

        Hiding the control on an untraced answer would hide it almost always (a trace id
        needs a recording provider, and no deployed environment wires the Langfuse
        exporter) and, worse, would make it flicker in and out between answers for
        reasons no seller can see. The click is accepted and shown as given either way;
        whether anything reaches the sink is `feedbackPolicy`'s problem and an
        operator's, not hers. Deliberate, documented degradation — not a missing guard.

        ## The two are SYMMETRIC

        Same size, same placement, same single click, no confirmation and no "tell us
        more" step on either. Negative feedback is the half worth collecting, so it must
        not be the more expensive one to give.
      */}
      {!message.isLoading && message.content && !message.commandPlan && (
        <div className={classes.feedbackButtons}>
          <Tooltip label={t( 'ai_assistant_helpful' )} zIndex={99999}>
            <ActionIcon
              variant={message.feedback === 'up' ? 'light' : 'subtle'}
              size="xs"
              color={message.feedback === 'up' ? 'teal' : 'gray'}
              aria-pressed={message.feedback === 'up'}
              data-testid={componentsTestIds.aiChatFeedbackUp( message.id )}
              onClick={() => submitFeedback( message.id, 'up' )}
            >
              <IconThumbUp size={14} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t( 'ai_assistant_not_helpful' )} zIndex={99999}>
            <ActionIcon
              variant={message.feedback === 'down' ? 'light' : 'subtle'}
              size="xs"
              color={message.feedback === 'down' ? 'red' : 'gray'}
              aria-pressed={message.feedback === 'down'}
              data-testid={componentsTestIds.aiChatFeedbackDown( message.id )}
              onClick={() => submitFeedback( message.id, 'down' )}
            >
              <IconThumbDown size={14} />
            </ActionIcon>
          </Tooltip>
        </div>
      )}
    </div>

    <div className={`${classes.timestamp} ${classes.assistantTimestamp}`}>
      {formatTimestamp( message.timestamp )}
    </div>
  </div>;
};

export interface AIAssistantChatProps {
  className?: string;
}

export function AIAssistantChat({ className }: AIAssistantChatProps ) {
  const messages = useAIAssistantSelector( s => s.messages );
  const isProcessing = useAIAssistantSelector( s => s.isProcessing );
  const conversationHistory = useAIAssistantSelector( s => s.conversationHistory );
  const isLoadingHistory = useAIAssistantSelector( s => s.isLoadingHistory );
  const conversationId = useAIAssistantSelector( s => s.conversationId );
  // Fullscreen constrains the thread and the composer to a centred column.
  // Without it, lines run the full viewport width and become unreadable — that
  // is the difference between "fullscreen" and "a stretched panel".
  const panelMode = useAIAssistantSelector( s => s.panelMode );
  const isFullscreen = panelMode === 'fullscreen';
  const { sendMessage, clearMessages, abortAssistantResponse } = useAIAssistantActions();
  const { t } = useTranslation( 'interactions' );
  const [ inputValue, setInputValue ] = useState( '' );
  const [ showHistory, setShowHistory ] = useState( false );

  const formatTimestamp = ( timestamp: number ) => {
    const date = new Date( timestamp );
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleSendMessage = () => {
    if ( inputValue.trim() === '' || isProcessing ) return;
    sendMessage( inputValue );
    setInputValue( '' );
  };

  const handleKeyPress = ( e: React.KeyboardEvent ) => {
    if ( e.key === 'Enter' && !e.shiftKey ) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = () => {
    setShowHistory( false );
    clearMessages();
  };

  const handleHistoryToggle = () => {
    setShowHistory( !showHistory );
  };

  const messagesEndRef = useRef<HTMLDivElement>( null );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * Fullscreen turns history into a permanent left rail (ChatGPT / Gemini
   * shape) instead of a view that replaces the thread. There is room for both,
   * and hiding the conversation you are in to look at the list is the wrong
   * trade the moment the panel is this wide.
   *
   * Floating keeps the toggle: 400px cannot carry two columns.
   */
  const sidebar = (
    <aside className={classes.sidebar} data-testid={componentsTestIds.aiChatSidebar}>
      <Button
        variant="subtle"
        color="gray"
        size="xs"
        justify="flex-start"
        leftSection={<IconMessagePlus size={16} />}
        className={classes.newChatButton}
        onClick={handleNewChat}
        data-testid={componentsTestIds.aiChatSidebarNewChat}
      >
        {t( 'ai_assistant_new_chat' )}
      </Button>

      <Text className={classes.sidebarSectionTitle}>
        {t( 'ai_assistant_past_conversations' )}
      </Text>

      <div className={classes.sidebarList}>
        {isLoadingHistory && conversationHistory.length === 0 && (
          <Text size="xs" c="dimmed" ta="center" py={16}>
            {t( 'ai_assistant_history_loading', 'Cargando…' )}
          </Text>
        )}
        {!isLoadingHistory && conversationHistory.length === 0 && (
          <div className={classes.sidebarEmpty}>
            <div className={classes.sidebarEmptyIcon}><IconMessage size={18} /></div>
            <Text className={classes.sidebarEmptyTitle}>
              {t( 'ai_assistant_history_empty_title', 'Todavía no hay conversaciones' )}
            </Text>
            <Text className={classes.sidebarEmptyDescription}>
              {t( 'ai_assistant_history_empty_hint', 'Las que tengas con Tessy van a aparecer acá' )}
            </Text>
          </div>
        )}
        {conversationHistory.map(( conv ) => (
          <div
            key={conv.id}
            className={`${classes.sidebarItem} ${conv.id === conversationId ? classes.sidebarItemActive : ''}`}
            data-testid={componentsTestIds.aiChatSidebarItem( conv.id )}
            title={`${new Date( conv.updatedAt ).toLocaleDateString()} · ${conv.messageCount} ${
              conv.messageCount === 1 ? t( 'ai_assistant_step' ) : t( 'ai_assistant_steps' )
            }`}
          >
            <Text size="xs" lineClamp={1} className={classes.sidebarItemTitle}>
              {conv.title || t( 'ai_assistant_untitled_conversation', 'Conversación' )}
            </Text>
          </div>
        ))}
      </div>
    </aside>
  );

  return (
    <div
      className={`${classes.chatContainer} ${isFullscreen ? classes.fullscreen : ''} ${className || ''}`}
      data-panel-mode={panelMode}
    >
      {isFullscreen && sidebar}
      <div className={classes.chatMain}>
      {/* In fullscreen both actions live in the sidebar — keeping them here too
          would offer the same two actions twice on one screen. */}
      {!isFullscreen && (
        <div className={classes.headerActions}>
          <Tooltip zIndex={9999} label={t( 'ai_assistant_new_chat' )}>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={handleNewChat}
              aria-label={t( 'ai_assistant_new_chat' )}
            >
              <IconMessagePlus size={18} />
            </ActionIcon>
          </Tooltip>
          <Tooltip zIndex={9999} label={t( 'ai_assistant_chat_history' )}>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={handleHistoryToggle}
              aria-label={t( 'ai_assistant_chat_history' )}
            >
              <IconHistory size={18} />
            </ActionIcon>
          </Tooltip>
        </div>
      )}

      {/* The replace-the-thread history view is a floating-only affordance. */}
      {showHistory && !isFullscreen ? (
        <div className={classes.conversationsList}>
          <Text size="sm" fw={500} mb={12} className={classes.sectionTitle}>
            {t( 'ai_assistant_past_conversations' )}
          </Text>
          <div className={classes.conversationItems}>
            {conversationHistory.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" py={16}>—</Text>
            )}
            {conversationHistory.map( conv => (
              <div key={conv.id} className={classes.conversationItem}>
                <IconMessage size={16} className={classes.conversationIcon} />
                <div className={classes.conversationText}>
                  <Text size="sm" lineClamp={1}>{conv.title}</Text>
                  <Text size="xs" c="dimmed">
                    {new Date( conv.updatedAt ).toLocaleDateString()} · {conv.messageCount} {conv.messageCount === 1 ? t( 'ai_assistant_step' ) : t( 'ai_assistant_steps' )}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={classes.messagesContainer}>
          {messages.map(( message ) => (
            message.sender === 'user' ? (
              <UserMessage key={message.id} message={message} formatTimestamp={formatTimestamp} />
            ) : (
              <AssistantMessage key={message.id} message={message} formatTimestamp={formatTimestamp} />
            )
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/*
        HITL approval surface. Driven by `s.pendingApproval`, which the
        interrupt StreamUpdate populates (TV1-1826 MI1). Rendered once here in
        the panel body — it is a per-conversation slice, not per-message — and
        self-hides (returns null) when no approval is pending.
      */}
      <ApprovalCard />

      <div className={classes.autoExecuteSection}>
        <AutoExecuteToggle />
      </div>

      <div className={classes.inputContainer}>
        <div className={classes.textareaWrapper}>
          <Textarea
            placeholder={t( 'ai_assistant_type_message' )}
            value={inputValue}
            onChange={( e ) => setInputValue( e.currentTarget.value )}
            onKeyDown={handleKeyPress}
            autosize
            minRows={1}
            maxRows={5}
            className={classes.messageInput}
            data-testid={componentsTestIds.aiChatInput}
          />
        </div>
        <div className={classes.inputToolbar}>
          <Button
            leftSection={<IconHash size={16} />}
            variant="subtle"
            color="gray"
            aria-label={t( 'ai_assistant_context' )}
            className={classes.contextButton}
            size="compact-xs"
          >
            {t( 'ai_assistant_context' )}
          </Button>
          <ActionIcon
            variant="subtle"
            onClick={isProcessing ? abortAssistantResponse : handleSendMessage}
            disabled={inputValue.trim() === '' && !isProcessing}
            aria-label={isProcessing ? t( 'ai_assistant_stop_generation' ) : t( 'ai_assistant_send_message' )}
            className={classes.sendButton}
            color={isProcessing ? 'gray' : 'primary.5'}
            data-testid={componentsTestIds.aiChatSend}
          >
            {isProcessing ? <IconPlayerStopFilled size={16} /> : <IconSend size={16} />}
          </ActionIcon>
        </div>
      </div>
      </div>
    </div>
  );
}

export default AIAssistantChat;
